/* <lab-reactive-bg> — pointer-reactive canvas background for The Lab NA.
   Variants: dots | plus | lines. Attributes: variant, intensity (0–1), motion (true/false).
   Decorative only — pointer-events:none; listens on window and maps coords. */
(function () {
  if (customElements.get('lab-reactive-bg')) return;

  const GREEN = { r: 96, g: 186, b: 70 };
  const REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LabReactiveBg extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.style.display = 'block';
      this.style.pointerEvents = 'none';
      if (!this.style.position) this.style.position = 'absolute';
      if (!this.style.width && !this.style.inset) { this.style.inset = '0'; }
      const c = document.createElement('canvas');
      c.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;';
      this.appendChild(c);
      this._canvas = c;
      this._ctx = c.getContext('2d');
      this._px = -9999; this._py = -9999;
      this._sx = -9999; this._sy = -9999; /* smoothed pointer */
      this._t = Math.random() * 100;
      this._ro = new ResizeObserver(() => this._resize());
      this._ro.observe(this);
      this._onMove = (e) => {
        const r = this.getBoundingClientRect();
        this._px = e.clientX - r.left;
        this._py = e.clientY - r.top;
      };
      this._onEnd = () => { this._px = -9999; this._py = -9999; };
      window.addEventListener('pointermove', this._onMove, { passive: true });
      window.addEventListener('pointerdown', this._onMove, { passive: true });
      document.addEventListener('pointerleave', this._onEnd);
      this._resize();
      this._loop = this._loop.bind(this);
      this._raf = requestAnimationFrame(this._loop);
    }

    disconnectedCallback() {
      this._init = false;
      cancelAnimationFrame(this._raf);
      if (this._ro) this._ro.disconnect();
      window.removeEventListener('pointermove', this._onMove);
      window.removeEventListener('pointerdown', this._onMove);
      document.removeEventListener('pointerleave', this._onEnd);
    }

    _resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this._w = this.clientWidth;
      this._h = this.clientHeight;
      this._canvas.width = Math.max(1, this._w * dpr);
      this._canvas.height = Math.max(1, this._h * dpr);
      this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    _loop() {
      this._raf = requestAnimationFrame(this._loop);
      const ctx = this._ctx;
      if (!ctx || !this._w || !this._h) return;

      const variant = this.getAttribute('variant') || 'dots';
      const intensity = Math.max(0, Math.min(1, parseFloat(this.getAttribute('intensity') || '0.75')));
      this._col = this.getAttribute('color') || '96,186,70';
      const motion = this.getAttribute('motion') !== 'false' && !REDUCED;

      if (motion) this._t += 0.016;
      const mode = this.getAttribute('mode') || 'pointer';
      if (mode === 'scroll') {
        if (!this._scroller || !this._scroller.isConnected) {
          const frame = this.closest('[data-screen-label]');
          this._scroller = frame ? frame.querySelector('[data-lab-scroll]') : null;
        }
        let p = 0;
        if (this._scroller) {
          const max = this._scroller.scrollHeight - this._scroller.clientHeight;
          p = max > 0 ? this._scroller.scrollTop / max : 0;
        }
        const tx = this._w * (0.5 + 0.28 * Math.sin(p * Math.PI * 2));
        const ty = -60 + p * (this._h + 120);
        if (this._sx < -999) { this._sx = tx; this._sy = ty; }
        this._sx += (tx - this._sx) * 0.12;
        this._sy += (ty - this._sy) * 0.12;
      } else {
        const k = 0.18;
        if (this._px < -999) { this._sx += (-9999 - this._sx) * 0.08; this._sy = this._sx; }
        else { this._sx += (this._px - this._sx) * k; this._sy += (this._py - this._sy) * k; }
      }

      ctx.clearRect(0, 0, this._w, this._h);

      if (variant === 'lines') this._drawLines(ctx, intensity);
      else this._drawGrid(ctx, variant, intensity);
    }

    _drawGrid(ctx, variant, intensity) {
      const gap = 30;
      const glowR = 170;
      const t = this._t;
      const ox = ((this._w % gap) / 2) + gap / 2;
      const oy = ((this._h % gap) / 2) + gap / 2;
      for (let x = ox; x < this._w; x += gap) {
        for (let y = oy; y < this._h; y += gap) {
          const shimmer = 0.018 * Math.sin(t * 0.9 + (x + y) * 0.02);
          const base = 0.055 + shimmer;
          const dx = x - this._sx, dy = y - this._sy;
          const d = Math.sqrt(dx * dx + dy * dy);
          const near = Math.max(0, 1 - d / glowR);
          const glow = near * near * intensity;

          if (variant === 'plus') {
            const arm = 2.5 + glow * 1.5;
            ctx.strokeStyle = glow > 0.04
              ? 'rgba(' + this._col + ',' + (base + glow * 0.6).toFixed(3) + ')'
              : 'rgba(255,255,255,' + base.toFixed(3) + ')';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x - arm, y); ctx.lineTo(x + arm, y);
            ctx.moveTo(x, y - arm); ctx.lineTo(x, y + arm);
            ctx.stroke();
          } else {
            const r = 1 + glow * 1.2;
            ctx.fillStyle = glow > 0.04
              ? 'rgba(' + this._col + ',' + (base + glow * 0.65).toFixed(3) + ')'
              : 'rgba(255,255,255,' + base.toFixed(3) + ')';
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    _drawLines(ctx, intensity) {
      const gap = 90;
      const ox = (this._w % gap) / 2;
      for (let x = ox; x <= this._w; x += gap) {
        ctx.strokeStyle = 'rgba(255,255,255,0.035)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, this._h);
        ctx.stroke();
        const near = Math.max(0, 1 - Math.abs(x - this._sx) / (gap * 1.6));
        if (near > 0.02) {
          const a = near * near * 0.25 * intensity;
          const col = this._col || '96,186,70';
          const grad = ctx.createLinearGradient(0, 0, 0, this._h);
          const cy = Math.max(0, Math.min(1, this._sy / this._h));
          grad.addColorStop(Math.max(0, cy - 0.3), 'rgba(' + col + ',0)');
          grad.addColorStop(cy, 'rgba(' + col + ',' + a.toFixed(3) + ')');
          grad.addColorStop(Math.min(1, cy + 0.3), 'rgba(' + col + ',0)');
          ctx.strokeStyle = grad;
          ctx.beginPath();
          ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, this._h);
          ctx.stroke();
        }
      }
    }
  }

  customElements.define('lab-reactive-bg', LabReactiveBg);
})();
